import { Fragment, lazy, Suspense, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router'
import { ErrorBoundary } from '@parkify/ui/error-boundary'
import Sidebar, { OPS_RAIL_COLLAPSED, OPS_RAIL_CONTENT_GAP, OPS_RAIL_OPEN, type NavGroup, type NavItem } from './components/Sidebar'
import { ScreenErrorFallback } from './components/ErrorFallbacks'
import RestrictedScreen from './components/RestrictedScreen'
import AveryWidget, { AskAveryBar, askAvery } from './components/AveryWidget'
import KbCandidatesWidget from './components/KbCandidatesWidget'
import Walkthrough from './components/Walkthrough'
import ViewAsBanner from './components/ViewAsBanner'
import ClientSessionBanner from './components/ClientSessionBanner'
import { useAuth } from './auth/AuthContext'
import VerifyLink from './auth/VerifyLink'
import { isKillSwitchAdmin } from './auth/killSwitchAdmins'
import { DEMO } from './demo'
import { isDemoTabHidden } from './demo/tabVisibility'
import { trackUsage } from './utils/usage'


// Screens are code-split — each becomes its own chunk so the initial bundle
// loads the sidebar + auth + whatever screen the URL points to, not all 15
// screens at once. Drops cold-load JS from ~605KB to ~250KB.
//
// Support.tsx and ParkifyAdmin.tsx are the heaviest chunks (3,546 + 986 LOC
// respectively); the lazy boundary keeps them out of the critical path for
// the 80% of operators who land on Home or Customers most days.
//
// NOTE: Support is a Parkify-internal tool — it's wired below but gated to
// Parkify staff only (PARKIFY_STAFF_ONLY), so client-scoped operators never
// see it even at owner role. It triages help@parkify.com email, which is
// Parkify's own support function, not a per-tenant feature.
const Avery         = lazy(() => import('./screens/Avery'))
const Home          = lazy(() => import('./screens/Home'))
const Support       = lazy(() => import('./screens/Support'))
const IntakeSubmissions = lazy(() => import('./screens/IntakeSubmissions'))
const ParkifyAdmin  = lazy(() => import('./screens/ParkifyAdmin'))
const ClientAdmin   = lazy(() => import('./screens/ClientAdmin'))
const Integrations  = lazy(() => import('./screens/Integrations'))
const Zones         = lazy(() => import('./screens/Zones'))
const Validations   = lazy(() => import('./screens/Validations'))
const Tenants       = lazy(() => import('./screens/Tenants'))
const Analytics     = lazy(() => import('./screens/Analytics'))
const Transactions  = lazy(() => import('./screens/Transactions'))
const QR            = lazy(() => import('./screens/QR'))
const Permits       = lazy(() => import('./screens/Permits'))
const Accounting    = lazy(() => import('./screens/Accounting'))
const Customers     = lazy(() => import('./screens/Customers'))
const Subscriptions = lazy(() => import('./screens/Subscribers'))
const Enforcement   = lazy(() => import('./screens/Enforcement'))
const Disputes      = lazy(() => import('./screens/Disputes'))
const KpiRevenue    = lazy(() => import('./screens/KpiRevenue'))
const KillSwitches  = lazy(() => import('./screens/KillSwitches'))
const RefundRequests = lazy(() => import('./screens/RefundRequests'))

// Routes whose title row renders the Ask Avery bar itself, inline (flush-right,
// on the title's line) — either because the row is otherwise empty on the
// right (Home, Analytics, Support) or because a `flex:1` spacer pushes the bar
// past the row's other controls (Zones, Tenants, Validations, Customers,
// Enforcement, ParkifyAdmin, ClientAdmin, Transactions) so it doesn't collide
// with them — the flush-right slot on those rows is the bar's, which on
// Transactions meant moving the channel/zone filters left of the spacer.
// Those screens import AskAveryBar themselves; App must NOT also render the
// global bar for them, or it'd double up. Every other screen has no search
// context in its title row (or keeps filters in a lower row), so the global
// bar stays on its own slim row above the content (see below).
// Keep this in sync if a screen's header gains or sheds right-aligned controls
// — src/__tests__/averyBarSelfRender.test.ts fails loudly if it drifts.
const AVERY_BAR_SELF_RENDER_ROUTES = new Set<string>([
  '/', '/analytics', '/support', '/zones', '/transactions',
  '/tenants', '/validations', '/customers', '/enforcement',
  '/admin/parkify', '/admin/client',
])

// Routes with NO Ask Avery bar at all — distinct from the set above, which only
// says "the screen renders the bar itself". Today that's Avery's own page: a
// one-line box that opens a chat, sitting directly above an open chat with a
// real composer, is two inputs for one job. App's global <AveryWidget/> is
// suppressed there too — that one is
// load-bearing, not cosmetic: the page mounts its own inline widget, and two
// mounted widgets would run two copies of the same chat state against one
// localStorage key and double every notification poll.
const AVERY_SURFACE_SUPPRESSED_ROUTES = new Set<string>(['/avery'])


// The permission each screen requires (SCREEN_PERMS) lives in
// auth/screenAccess.ts with the rest of the screen-access rule (canOpenScreen).

// Map each sidebar section to the optional per-tenant feature flag that
// gates it. A section without an entry is always visible (subject only to
// the permission map above). A section with an entry shows only when the
// caller's tenant hasn't switched the flag off on Client.features.
//
// Set these from Parkify Admin → Clients → (edit a client) → Feature flags.
// The map itself now lives in auth/screenAccess.ts, next to PARKIFY_STAFF_ONLY,
// so it is reachable and testable without importing this whole module.

// URL path for each screen. Drives deep-linking, browser back, and the
// sidebar highlight. Home is "/" so cold loads land there naturally.
// Admin screens namespaced under /admin/* so the two never collide.
const SCREEN_PATHS: Record<ScreenId, string> = {
  home:          '/',
  avery:         '/avery',
  support:       '/support',
  intake_submissions: '/intake/submissions',
  transactions:  '/transactions',
  zones:         '/zones',
  qr:            '/qr',
  permits:       '/permits',
  validations:   '/validations',
  tenants:       '/tenants',
  enforcement:   '/enforcement',
  accounting:    '/accounting',
  analytics:     '/analytics',
  customers:     '/customers',
  subscriptions: '/subscriptions',
  disputes:      '/disputes',
  integrations:  '/integrations',
  parkify_admin: '/admin/parkify',
  client_admin:  '/admin/client',
  kpi_revenue:   '/revenue',
  kill_switches: '/admin/kill-switches',
  refund_requests: '/refund-requests',
}

// Reverse lookup — for sidebar highlight + perm bounce.
const PATH_TO_SCREEN: Record<string, ScreenId> = Object.fromEntries(
  Object.entries(SCREEN_PATHS).map(([id, path]) => [path, id as ScreenId]),
)

// Grouped IA (console-home-cockpit): five operator AREAS + a visually
// separated Parkify-internal block, instead of the flat 13–18-item list.
// Groups are presentation only — each child keeps its ScreenId, URL,
// permission and feature-flag gates. A leaf (children: []) navigates itself.
//
// Avery is BOTH ambient and a place. She stays ambient — the bar on every
// screen, plus ⌘K — and she is also a nav destination again,
// because "ambient only" left the full-page chat reachable exclusively by a URL
// nobody types (reported at the 2026-07-29 product sync: the surface was built,
// worked, and was invisible). The nav entry lands on `/avery`, where the chat is
// the screen rather than a drawer over one; `/ask` keeps the chrome-less variant
// for deep links.
const NAV_GROUPS: NavGroup[] = [
{
    id: 'home', label: 'Home', children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>,
  },
  {
    // Sparkle, not a speech bubble — Support already owns the bubble in this
    // same sidebar and the two would read as the same thing at 16px.
    id: 'avery', label: 'Ask Avery', children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/>
      <path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/>
    </svg>,
  },
  {
    // "Facility" was doing four jobs at once — physical config, commercial
    // relationships, access grants and enforcement — which is why it had six
    // children and still felt arbitrary. Dissolved: the physical lot and its
    // codes stand on their own, the commercial relationships group under
    // Tenants, and enforcement is its own domain.
    //
    // A client has never once called their building a zone: Halcyon Center is
    // the property, NC2600-A is a payable area inside it. Locations is the word
    // they use.
    id: 'zones', label: 'Locations', children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z"/>
      <circle cx="12" cy="10" r="2.5"/>
    </svg>,
  },
  {
    // "Payments" was a folder Transactions, Disputes and Accounting got filed
    // into for lack of anywhere better. Financials is what an operator calls the
    // question these four answer: what came in, what went out, what's disputed.
    id: 'payments', label: 'Financials',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>,
    children: [
      { id: 'transactions', label: 'Transactions', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h12v20l-3-2-3 2-3-2-3 2z"/><path d="M9 8h6M9 12h6"/></svg> },
      { id: 'accounting', label: 'Accounting', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10l9-6 9 6"/><path d="M5 10v9M12 10v9M19 10v9"/><path d="M3 20h18"/></svg> },
      // Stays here rather than moving to the internal block: it is already
      // PARKIFY_STAFF_ONLY, so an operator never sees it, and for the staff who
      // do it belongs next to the money it disputes.
      { id: 'disputes', label: 'Disputes', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg> },
    ],
  },
  {
    id: 'analytics', label: 'Analytics', children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>,
  },
  {
    // The commercial half of what "Facility" used to hold: who leases here, what
    // they grant their people, and what they comp. All three are one tenant
    // relationship seen from three angles, which is why they group.
    id: 'tenantgrp', label: 'Tenants',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
    </svg>,
    children: [
      { id: 'tenants', label: 'Tenants', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16"/><path d="M13 9h7a1 1 0 0 1 1 1v11"/><path d="M2 21h20"/><path d="M6.5 8h3M6.5 12h3M6.5 16h3M16.5 13h1.5M16.5 17h1.5"/></svg> },
      { id: 'permits', label: 'Permits', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="11" r="2"/><path d="M5.5 16.5a3 3 0 0 1 6 0"/><path d="M15 10h4M15 14h3"/></svg> },
      { id: 'validations', label: 'Validations', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4z"/><path d="M9 10l2 2 4-4"/></svg> },
    ],
  },
  {
    // Top-level rather than a child of Location: a subscription is not a permit.
    // A permit is granted BY a tenant to their staff (B2B, under Location); a
    // subscription is bought by any driver at checkout and charged automatically,
    // with no invoice and no receivable. Different counterparty, different money
    // flow, so it gets its own place rather than being filed next to Permits.
    //
    // The icon is the driver's monthly-pass mark (a calendar with a dollar
    // badge): the operator is looking at the same product the driver bought, so
    // it is the same glyph. The white ring keeps the calendar's corner from
    // showing through the badge.
    id: 'subscriptions', label: 'Subscriptions', children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 2.5v3M16 2.5v3"/><rect x="3" y="4.5" width="15" height="15" rx="2.3"/><path d="M3 9.5h15"/>
        <circle cx="7" cy="13" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="11" cy="13" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="7" cy="16.5" r="0.8" fill="currentColor" stroke="none"/>
      </g>
      <circle cx="18" cy="17.5" r="6.3" fill="var(--color-white)"/>
      <circle cx="18" cy="17.5" r="5.2" fill="currentColor"/>
      <g stroke="var(--color-white)" strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13.7v7.6"/>
        <path d="M20 15.4c0-.85-.9-1.3-2-1.3s-2 .45-2 1.3.9 1.25 2 1.65 2 .8 2 1.65-.9 1.3-2 1.3-2-.45-2-1.3"/>
      </g>
    </svg>,
  },
  {
    // Its own domain rather than a child of the lot: enforcement is a different
    // job, done by different people, often an outside partner.
    id: 'enforcement', label: 'Enforcement', children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 4v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V7z"/><path d="M9 12l2 2 4-4"/>
    </svg>,
  },
  {
    // Client Admin rebranded "Settings" in the nav — team, branding, Stripe,
    // feature flags. Same screen, same /admin/client URL, same gates.
    id: 'client_admin', label: 'Settings', children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>,
  },
  // ─── Parkify internal (staff only — every child is in PARKIFY_STAFF_ONLY,
  // so the whole block vanishes for client-scoped operators) ────────────────
  {
    // Owner decision, 2026-10-13: clients use the QR card on each location's
    // zone Overview. Bulk printing and artwork management stay with Parkify.
    id: 'qr', label: 'QR codes', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20h1"/>
    </svg>,
  },
  {
    id: 'support', label: 'Support', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>,
  },
  {
    id: 'intake_submissions', label: 'Intake submissions', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/>
    </svg>,
  },
  {
    // Sits next to Support rather than as a tab inside it: refund_requests is
    // its OWN lazy chunk (PERF-BUDGETS.md — Support is already the heaviest
    // screen in this app), and it reads a Parkify-internal-only table (see
    // auth/screenAccess.ts) so it can't share Support's per-tenant nav slot.
    id: 'refund_requests', label: 'Refund Requests', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 4v8h8"/><path d="M12 8v4l3 3"/>
    </svg>,
  },
  {
    id: 'customers', label: 'Customers', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>,
  },
  {
    id: 'kpi_revenue', label: 'Platform analytics', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l6-6 4 4 7-7"/><path d="M14 8h6v6"/>
    </svg>,
  },
  {
    id: 'integrations', label: 'Integrations', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
    </svg>,
  },
  {
    id: 'parkify_admin', label: 'Parkify Admin', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
      <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
    </svg>,
  },
  {
    id: 'kill_switches', label: 'Kill Switches', internal: true, children: [],
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <line x1="12" y1="7" x2="12" y2="12"/>
    </svg>,
  },
]

// Flat id+label manifest derived from NAV_GROUPS, for the demo build's
// "customize tabs for this call" picker (src/demo/switcher.ts) — kept as a
// derived export rather than a hand-maintained second list, so the picker
// can never drift from the real nav the way the demo's mock routes once did
// (see docs/ENFORCEMENT-ACCESS-BRIEF.md). `internal` groups are excluded:
// every child is staff-only and never renders for the demo's client-scoped
// persona regardless, so offering to hide them would be a no-op checkbox.
export const DEMO_NAV_TAB_OPTIONS: { id: string; label: string; group: string }[] =
  NAV_GROUPS.filter(g => !g.internal).flatMap(g => (
    g.children.length > 0
      ? g.children.map(c => ({ id: c.id, label: c.label, group: g.label }))
      : [{ id: g.id, label: g.label, group: g.label }]
  ))

import { MOBILE_BREAKPOINT, useViewportWidth } from './hooks/useViewport'
// Screen access rules live in auth/screenAccess.ts so the sidebar, the
// direct-URL guard, Home's attention strip, and the global search bar all read
// ONE definition — see the note there.
import {
  ALLOWLIST_ONLY, canOpenScreen, PARKIFY_STAFF_ONLY, SCREEN_FEATURE_FLAG, SCREEN_PERMS, type ScreenId,
} from './auth/screenAccess'
import { API_BASE } from './config/apiBase'



// Chrome-less full-bleed Avery (OMNI-2, `/ask`) — for an operator who wants
// only Avery, no nav/sidebar. Reuses TWO existing pieces verbatim, no new
// chat UI: the `Avery` landing screen (hero + recent threads + action tiles)
// as the full-viewport body, and the `AveryWidget` side-drawer opened on
// mount over it. AveryWidget owns its own `open` state, so if the operator
// closes the drawer they land on the branded `Avery` screen, not blank space
// — that screen, not a corner button, is what's underneath.
function AskAveryPage() {
  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'auto', background: 'var(--color-gray-50)', padding: '48px 24px' }}>
      <Avery />
      <AveryWidget />
    </div>
  )
}

export default function App() {
  const {
    user, authHeaders, signOut, signInGoogle, googleConfigured, requestEmailLink, verifyEmailLink, loading, error,
    hasPerm, hasFeature, viewingAs, realUser, stopViewingAs, clientSession, exitClientSession, switchClient,
  } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // Current screen is derived from the URL — every nav action ends up as
  // a route change, and every route change re-renders this. Unknown paths
  // fall back to 'home' so the sidebar always highlights something.
  // Normalize trailing slash so /zones/ matches /zones (cold-load
  // bookmarks ending in / would otherwise miss PATH_TO_SCREEN).
  const cleanPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '')
  const currentScreen: ScreenId = PATH_TO_SCREEN[cleanPath] ?? 'home'

  // The Internal Usage tab reads these daily rollups. Keep this at the route
  // shell so every screen, including lazy-loaded screens, is counted once per
  // route change without blocking navigation.
  useEffect(() => {
    // Not under View as: the target didn't visit anything, and the worker
    // refuses the write under a lens anyway (it would only add audit noise).
    if (!user || viewingAs || cleanPath === '/verify' || cleanPath === '/ask') return
    trackUsage(API_BASE, authHeaders(), 'screen_view', { screen: currentScreen })
  }, [user, viewingAs, cleanPath, currentScreen, authHeaders])
  const viewportWidth = useViewportWidth()
  const isMobile = viewportWidth < MOBILE_BREAKPOINT
  const mainRef = useRef<HTMLElement>(null)

  // The app shell, not the document, owns vertical scrolling. React Router
  // keeps that <main> mounted between screens, so without an explicit reset a
  // route opened at Home's old scroll offset. On a tall iPhone Max that could
  // put the menu + wordmark above the visual viewport and make the next screen
  // look cropped. Treat every pathname as a fresh console page.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [cleanPath])

  // Sidebar starts closed on mobile (it'd cover most of the viewport),
  // open on desktop. Tracked separately so the user's choice during a
  // session sticks.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.innerWidth >= MOBILE_BREAKPOINT
  })

  // Per-screen visibility: permission (role) AND tenant feature flag AND the
  // staff/allowlist gates. Same rules as the old flat list — the grouping is
  // presentation only.
  const canSee = useMemo(() => (id: ScreenId): boolean => {
    if (!canOpenScreen(id, { hasPerm, hasFeature, user })) return false
    // Demo-only: tabs an operator hid via the floating switcher's "Customize
    // tabs" picker, to tailor which sections a prospect sees on a call (e.g.
    // dropping Enforcement for someone with no enforcement vendor). DEMO
    // constant-folds to `false` in real production builds, so this whole
    // check — and isDemoTabHidden's localStorage read — compiles away there.
    if (DEMO && isDemoTabHidden(id)) return false
    return true
  }, [hasPerm, hasFeature, user])

  // Groups for the sidebar: a leaf shows iff its own screen is visible; a
  // parent shows iff at least one child survives the filter.
  const visibleGroups = useMemo(() => NAV_GROUPS.flatMap((g): NavGroup[] => {
    if (g.children.length === 0) return canSee(g.id as ScreenId) ? [g] : []
    const children = g.children.filter(c => canSee(c.id as ScreenId))
    return children.length > 0 ? [{ ...g, children }] : []
  }), [canSee])

  // Flat visible-screen list — feeds the perm-bounce fallback below and the
  // Walkthrough tour (which walks screens, not groups).
  const visibleNav = useMemo<NavItem[]>(
    () => visibleGroups.flatMap(g => (g.children.length > 0 ? g.children : [g])),
    [visibleGroups],
  )

  // Validations explains a role refusal in place (Parity #39, VAL-03): the
  // content area shows RestrictedScreen instead of the screen, and the bounce
  // below is skipped. Any other reason (flag off, staff-only, allowlist) still
  // bounces, as before. Same condition drives the effect and the render.
  const validationsRefused = useMemo(() => {
    if (!user || currentScreen !== 'validations' || hasPerm(SCREEN_PERMS.validations)) return false
    const flag = SCREEN_FEATURE_FLAG.validations
    return !(flag && !hasFeature(flag))
      && !(PARKIFY_STAFF_ONLY.has('validations') && user.clientId != null)
      && !(ALLOWLIST_ONLY.has('validations') && !isKillSwitchAdmin(user.email))
  }, [user, currentScreen, hasPerm, hasFeature])

  // Bounce off a screen the user lost access to OR whose feature got
  // disabled on their tenant (role/flag changed mid-session). Uses replace
  // so the now-invalid URL doesn't sit in browser history.
  useEffect(() => {
    // /ask is a standalone chrome-less route handled separately below (its
    // own feature-flag redirect) — it isn't in PATH_TO_SCREEN, so skip this
    // screen-perm bounce entirely rather than let it fold to 'home' and
    // misfire a redirect off of /ask.
    if (!user || cleanPath === '/ask') return
    // Tenant gate: a client-scoped operator can't sit on a Parkify-internal
    // screen even via a direct URL (O9/D3). Named here only to pick the
    // fallback; the refusal itself is canOpenScreen, the sidebar's own rule.
    const staffBlocked = PARKIFY_STAFF_ONLY.has(currentScreen) && user.clientId != null
    if (!validationsRefused && !canOpenScreen(currentScreen, { hasPerm, hasFeature, user })) {
      const fallback = staffBlocked && currentScreen === 'qr' && canSee('zones')
        ? 'zones' : (visibleNav[0]?.id as ScreenId) ?? 'home'
      navigate(SCREEN_PATHS[fallback], { replace: true })
    }
  }, [user, cleanPath, currentScreen, hasPerm, hasFeature, canSee, visibleNav, navigate, validationsRefused])

  // Global Cmd+K / Ctrl+K palette shortcut (OMNI-2) — opens the existing
  // Avery widget from anywhere in the app via the same askAvery() escape
  // hatch AskAveryBar and the Avery screen already use, so there's no new
  // "open" state to keep in sync. Fires even while focus is in an input;
  // that's the expected palette-style behavior. Gated on BOTH the `avery`
  // feature flag and view.avery, the same pair the floating widget itself takes
  // below — it opens that very widget, so a shortcut that skipped the role check
  // would be a keyboard-shaped way around it.
  useEffect(() => {
    if (!user || !hasFeature('avery') || !hasPerm('view.avery')) return
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        askAvery()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [user, hasFeature, hasPerm])

  // Allow any screen to navigate via custom event (e.g. Zones → Integrations).
  // Kept as an escape hatch so existing dispatchers in Home / AveryWidget keep
  // working without each having to thread useNavigate.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ screen: ScreenId }>).detail
      const path = detail?.screen ? SCREEN_PATHS[detail.screen] : null
      if (path) navigate(path)
    }
    window.addEventListener('parkify:navigate', handler)
    return () => window.removeEventListener('parkify:navigate', handler)
  }, [navigate])

  // ── Sign-in + magic-link verify (pre-auth) ─────────────────────────────────
  if (!user) {
    // The email sign-in link lands here (?token=…). Traded for a session when
    // the person presses Continue (never on mount, see VerifyLink); on success
    // `user` flips non-null and the app renders normally.
    if (cleanPath === '/verify') {
      return <VerifyLink onVerify={verifyEmailLink} loading={loading} error={error} />
    }
    return (
      <SignIn
        onGoogle={signInGoogle}
        onEmailLink={requestEmailLink}
        googleEnabled={googleConfigured}
        loading={loading}
        error={error}
      />
    )
  }

  // Chrome-less full-bleed Avery (OMNI-2) — bypasses the sidebar/main shell
  // entirely rather than threading a "hide nav" flag through the whole
  // layout below. Still a real <Route> (matched via react-router) so the
  // URL contract is normal; it just renders outside the shell's <Routes>.
  // Bounces home unless this tenant has the `avery` feature on AND this role
  // holds view.avery — the same pair the floating widget takes.
  //
  // The perm half matters more here than anywhere else: rendering outside the
  // shell's <Routes> means this route also sits outside the screen-perm bounce
  // above, which explicitly skips /ask (it isn't in PATH_TO_SCREEN). So this
  // line is the ONLY role gate on the chrome-less surface — there is no second
  // one to fall back on.
  if (cleanPath === '/ask') {
    if (!hasFeature('avery') || !hasPerm('view.avery')) return <Navigate to="/" replace />
    return (
      <Suspense fallback={<ScreenLoading />}>
        <Routes>
          <Route path="/ask" element={<AskAveryPage />} />
        </Routes>
      </Suspense>
    )
  }

  // Auto-close sidebar on screen change (mobile only) — drawer behaviour.
  const goTo = (id: ScreenId) => {
    navigate(SCREEN_PATHS[id])
    if (isMobile) setSidebarOpen(false)
  }

  // First name for the walkthrough greeting (user is non-null past the guard).
  const firstName = user.name?.split(' ')[0] ?? user.email.split('@')[0] ?? 'there'

  return (
    <div style={{
      display: 'flex', height: '100vh', maxHeight: '100dvh',
      overflow: 'hidden', position: 'relative',
    }}>
      {/* Mobile drawer backdrop — dismisses sidebar on tap. */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'var(--glass-scrim)',
            zIndex: 999, animation: 'fadeIn 150ms ease',
          }}
          aria-hidden="true"
        />
      )}
      <Sidebar
          placement={isMobile ? 'drawer' : 'rail'}
          offscreen={isMobile && !sidebarOpen}
          groups={visibleGroups}
          currentId={currentScreen}
          onSelect={id => goTo(id as ScreenId)}
          open={isMobile ? true : sidebarOpen}
          onToggle={() => setSidebarOpen(o => !o)}
          // While viewing as someone the footer shows who YOU are, with the way
          // back above Sign out (see Sidebar's `lens`).
          userEmail={viewingAs ? (realUser?.email ?? user.email) : user.email}
          onSignOut={signOut}
          lens={viewingAs
            ? { label: `Viewing as ${viewingAs.email}`, exitLabel: 'Exit view', onExit: stopViewingAs }
            : clientSession
            ? { label: `Signed in as ${clientSession.clientName}`, exitLabel: 'Exit client session', onExit: exitClientSession }
            : undefined}
          onHome={() => goTo('home')}
          clientLogoUrl={user.client?.logoUrl}
          clientIconUrl={user.client?.iconUrl}
          clientName={user.client?.name}
          // A person in several clients picks which one this session is in.
          // Their own session only: never under View as or a client session.
          clientSwitcher={!viewingAs && !clientSession && !user.portfolio && user.clientId && (user.memberships?.length ?? 0) > 1
            ? { currentId: user.clientId, options: user.memberships!, onSwitch: id => void switchClient(id) }
            : undefined}
      />
      <main ref={mainRef} style={{
        flex: 1, minWidth: 0, maxWidth: '100%', overflowY: 'auto', overflowX: 'hidden',
        width: '100%',
        paddingTop: isMobile ? 14 : 28,
        paddingRight: isMobile ? 16 : 40,
        paddingBottom: isMobile ? 24 : 28,
        // Desktop content starts one rail-width past the left, plus the same 16px
        // the mobile shell uses. That leaves the 14px open peek over the canvas
        // and a gap after the rail's outer edge, so copy never sits under the
        // label column or flush against the chrome.
        paddingLeft: isMobile
          ? OPS_RAIL_CONTENT_GAP
          : (sidebarOpen ? OPS_RAIL_OPEN : OPS_RAIL_COLLAPSED) + OPS_RAIL_CONTENT_GAP,
        transition: 'padding-left 180ms ease',
      }}>
        {/* Mobile-only top bar — hamburger drawer toggle + brand wordmark.
            Sits inside <main>'s inner padding so it gets natural spacing
            from the screen edge; the gap between the button and the logo
            is generous so they don't read as one element. */}
        {isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            marginBottom: 12,
            paddingBottom: 4,
          }}>
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              style={{
                width: 44, height: 44, borderRadius: 10,
                background: 'var(--color-white)',
                border: '1px solid var(--color-gray-200)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', padding: 0, flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#231F20" strokeWidth="2.2" strokeLinecap="round">
                <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
            {/* Mobile brand mark — height bumped 20 → 34, then trimmed to 32
                (-5.9%, Austin 2026-10-21: "a tad too big") so the wordmark
                reads as the page anchor instead of an afterthought next
                to the menu button. The full-color master, same file as the
                sidebar header (Master Black wordmark, Master Purple Stack). */}
            <button
              onClick={() => goTo('home')}
              aria-label="Parkify console home"
              style={{
                display: 'flex', alignItems: 'center', padding: 0, border: 'none',
                background: 'none', cursor: 'pointer', flexShrink: 0,
              }}
            >
              <img
                src="/parkify-logo-color.svg" alt="Parkify"
                style={{ height: 32, flexShrink: 0 }}
              />
            </button>
          </div>
        )}
        {/* Read-only impersonation strip — present on every screen while an
            owner/admin is viewing-as another user. Renders nothing otherwise. */}
        <ViewAsBanner />
        {/* Real client-scoped session strip — staff "signed in as client".
            Opposite contract to view-as: writes are live. */}
        <ClientSessionBanner />
        {/* Global Ask Avery bar — own-row layout: a slim right-aligned row above
            the screen content. Used on data-table screens (Transactions, Zones,
            etc.) whose title row already carries filters + a search box on the
            right, where sitting the bar on the title's line would collide.

            Screens with a clear right-side gap in their title row (Home,
            Analytics — see AVERY_BAR_SELF_RENDER_ROUTES) instead render the bar
            INSIDE their own title row so it sits on the title's line. App skips
            the global bar for those to avoid rendering it twice.

            Rendered on mobile too. It used to be desktop-only because the
            bottom-right floating launcher was the mobile entry point; that
            launcher is gone (see AveryWidget), so hiding the bar here would
            leave these routes with NO way into Avery on a phone — Cmd+K is not
            a thing on a touch keyboard. */}
        {hasFeature('avery') && (!AVERY_BAR_SELF_RENDER_ROUTES.has(location.pathname) || validationsRefused)
          && !AVERY_SURFACE_SUPPRESSED_ROUTES.has(location.pathname) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <AskAveryBar />
          </div>
        )}
        {/* Screen-scoped boundary (parity with parkify-demo#91). Wraps ONLY the
            routed screen, so a render error in one screen leaves the sidebar,
            the banners and the Avery bar above on screen and swaps just this
            area for a fallback, instead of the root boundary replacing the
            whole console. resetKey is the pathname plus the view-as lens:
            picking another section (or switching lens) clears the error.
            Stale-chunk errors are rethrown to the root boundary in main.tsx,
            which owns the one-reload recovery. */}
        <ErrorBoundary
          label="screen"
          resetKey={`${location.pathname}|${viewingAs?.email ?? 'self'}`}
          fallback={ScreenErrorFallback}
        >
          <Suspense fallback={<ScreenLoading />}>
            {/* Keyed on the effective identity and session mode so entering,
                exiting or switching a client session or view-as lens forces
                a full remount of the active screen. Most screens load their
                data in a mount-once effect (loadZones, loadTransactions, etc.)
                that never re-runs on its own when the lens changes — without
                this key, switching identity (or exiting a lens) left the previous
                identity's data on screen until a hard browser reload. */}
            {validationsRefused ? <RestrictedScreen label="Validations" /> : <Routes key={JSON.stringify([user.email, user.clientId, viewingAs?.email ?? null, clientSession?.clientId ?? null])}>
              <Route path="/" element={user.role === 'intake_viewer' ? <Navigate to="/intake/submissions" replace /> : <Home />} />
              <Route path="/avery" element={<Avery variant="page" />} />
              <Route path="/support" element={<Support />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/zones" element={<Zones />} />
              <Route path="/qr" element={canSee('qr') ? <QR /> : <Navigate to={canSee('zones') ? '/zones' : '/'} replace />} />
              <Route path="/permits" element={<Permits />} />
              <Route path="/validations" element={<Validations />} />
              <Route path="/tenants" element={<Tenants />} />
              <Route path="/enforcement" element={<Enforcement />} />
              <Route path="/accounting" element={<Accounting />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/disputes" element={<Disputes />} />
              <Route path="/revenue" element={<KpiRevenue />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/admin/parkify" element={<ParkifyAdmin />} />
              <Route path="/intake/submissions" element={<IntakeSubmissions />} />
              <Route path="/admin/client" element={<ClientAdmin />} />
              <Route path="/admin/kill-switches" element={<KillSwitches />} />
              <Route path="/refund-requests" element={<RefundRequests />} />
              {/* Anything else (bad bookmark, removed route) lands on Home. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>}
          </Suspense>
        </ErrorBoundary>
      </main>
      {/* First-sign-in product tour — leads with Avery, then covers every
          section in THIS user's sidebar (visibleNav is already permission +
          feature filtered). Re-openable from Home's "Take a tour" link. */}
      <Walkthrough key={`walkthrough:${viewingAs?.email ?? 'self'}`} items={visibleNav} hasAvery={hasFeature('avery')} firstName={firstName} />
      {/* Avery's chat drawer — available on every screen to a role that holds
          view.avery, unless this tenant has the `avery` feature flag switched
          off in Client Admin. Renders nothing until something calls askAvery()
          (the omni bar, Cmd+K, the walkthrough); there is no longer a floating
          launcher in the corner. */}
      {/* Global drawer widget — skipped on /avery, which mounts its own inline
          instance (see AVERY_SURFACE_SUPPRESSED_ROUTES: two mounts = two chats). */}
      {/* hasPerm('view.avery') as well as the feature flag: the /avery SCREEN is
          permission-gated through SCREEN_PERMS, but this widget rides on every
          other route and was checking the tenant flag ONLY — so a role
          without view.avery lost the nav entry and kept the chat. The two axes
          are independent (flag = does this tenant buy Avery; perm = may this
          role use her) and both have to hold. */}
      {/* Keyed on the View as lens: a fresh widget per person, so its polls
          carry the right headers and its state is that person's (averyKey in
          components/avery/types). ⚠️ The prefix matters: Walkthrough above is a
          SIBLING keyed on the same lens, and two siblings sharing a key make
          React duplicate them (four stacked tours, live, 2026-09-25). */}
      {hasPerm('view.avery') && hasFeature('avery')
        && !AVERY_SURFACE_SUPPRESSED_ROUTES.has(location.pathname) && <Fragment key={`avery:${viewingAs?.email ?? 'self'}`}><AveryWidget /></Fragment>}
      {/* KB Candidates floating panel — surfaces auto-drafted KB entries
          from low-confidence triages. Parkify-staff only (clientId === null):
          the queue is the help@parkify.com support function, not a per-tenant
          feature, and each candidate carries Parkify customers' PII (caller
          name/email/plate/transcript). Gated like PARKIFY_STAFF_ONLY so a
          client-scoped operator never sees the launcher (O9/D3). */}
      {user.clientId == null && <KbCandidatesWidget />}
    </div>
  )
}

// ─── Screen-load fallback ────────────────────────────────────────────────────
// Shown briefly while a code-split chunk fetches. Hits cache on every
// subsequent visit, so this only flashes on first navigation to a given
// screen.

function ScreenLoading() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: 200, gap: 10, color: 'var(--color-gray-500)',
      fontSize: 12, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700,
    }}>
      {/* Renders inside the authenticated shell (every lazy screen load), so the
          tenant accent IS known here — unlike the pre-auth SignIn card and the
          main.tsx boot screen, which keep the literal Parkify purple. */}
      <div style={{
        width: 14, height: 14, borderRadius: '50%',
        border: '2px solid color-mix(in srgb, var(--parkify-accent) 20%, transparent)',
        borderTopColor: 'var(--parkify-accent)',
        animation: 'parkify-spin 0.7s linear infinite',
      }} />
      Loading
      <style>{`@keyframes parkify-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Sign-in screen ──────────────────────────────────────────────────────────

function SignIn({
  onGoogle, onEmailLink, googleEnabled, loading, error,
}: {
  onGoogle: (rememberMe: boolean) => void
  onEmailLink: (email: string, rememberMe: boolean) => Promise<boolean>
  // When false, no Google client id is configured — hide the Google button and
  // the OR divider, and offer only the email magic link.
  googleEnabled: boolean
  loading: boolean
  error: string | null
}) {
  // On by default: client users on a work machine were re-requesting a link every
  // morning. The box stays visible so a shared computer can untick it.
  const [remember, setRemember] = useState(true)
  // Email magic-link sub-flow. `sent` is the enumeration-safe confirmation —
  // shown whenever a well-formed request succeeded, regardless of whether the
  // address actually has an account (the server never tells us, by design).
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || sending) return
    setSending(true)
    const ok = await onEmailLink(email.trim(), remember)
    setSending(false)
    if (ok) setSent(true)
  }

  // "Remember me" governs BOTH Google and the email link, so it's shared. Its
  // position shifts: above the OR divider when Google is offered, below the
  // email form when the email link is the only option.
  const rememberField = (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px 12px', cursor: 'pointer' }}>
      <input
        type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#7F3F98' }}
      />
      <div>
        <div style={{ fontSize: 13, color: '#231F20', fontWeight: 600 }}>Remember me on this device</div>
        <div style={{ fontSize: 11, color: '#8a8595', marginTop: 2 }}>Stays signed in for days, not hours. Untick on a shared computer.</div>
      </div>
    </label>
  )

  return (
    <div style={{
      minHeight: '100vh',
      // One gate ground across every product — brand-tokens --color-gate-field.
      background: 'var(--color-gate-field, #F8F7F9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: "'Montserrat', 'Avenir', sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: '#ffffff',
        // One gate radius across products — brand-tokens --radius-gate (the
        // demo's ParkifyGate, 26px). Patrol's gate card uses the same token.
        borderRadius: 'var(--radius-gate, 26px)',
        padding: '40px 44px 36px',
        border: '1px solid var(--color-gray-300, #ECE9EF)',
        boxShadow: '0 12px 32px rgba(35,31,32,0.10), 0 2px 8px rgba(35,31,32,0.05)',
        color: '#231F20',
      }}>
        {/* Keep the product name as one brand lockup instead of stacking the
            console label beneath the Parkify logo. */}
        <div
          aria-label="Parkify Console"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            marginBottom: 28, whiteSpace: 'nowrap',
          }}
        >
          <img
            src="/parkify-logo-color.svg"
            alt=""
            aria-hidden="true"
            style={{ height: 32, width: 'auto', display: 'block' }}
          />
          <span style={{
            // The demo's product label: 12px/800 at .34em (brand-tokens
            // --tracking-gate-label), shared with patrol's gate.
            fontSize: 12, fontWeight: 800, letterSpacing: 'var(--tracking-gate-label, .34em)', color: '#7F3F98',
          }}>
            CONSOLE
          </span>
        </div>

        <h1 style={{
          fontSize: 30, fontWeight: 700, textAlign: 'center', margin: 0,
          letterSpacing: '-0.5px', color: '#231F20',
        }}>
          Sign in
        </h1>
        <p style={{
          fontSize: 14, color: '#5a5567', textAlign: 'center',
          marginTop: 8, marginBottom: 28, lineHeight: 1.55,
        }}>
          {googleEnabled
            ? 'Sign in with Google, or get a one-time link by email'
            : 'Get a one-time sign-in link by email'}
        </p>

        {googleEnabled && (
          <button
            onClick={() => onGoogle(remember)}
            disabled={loading}
            style={{
              width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-btn)',
              background: '#ffffff',
              border: '1.5px solid #ece9ef',
              color: '#231F20', fontFamily: 'inherit', fontWeight: 700, fontSize: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
              marginBottom: 10,
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'border-color 150ms, box-shadow 150ms',
            }}
            onMouseEnter={e => {
              if (loading) return
              const t = e.currentTarget as HTMLButtonElement
              t.style.border = '1.5px solid #7F3F98'
              t.style.boxShadow = '0 2px 10px rgba(127,63,152,0.18)'
            }}
            onMouseLeave={e => {
              const t = e.currentTarget as HTMLButtonElement
              t.style.border = '1.5px solid #ece9ef'
              t.style.boxShadow = 'none'
            }}
          >
            <GoogleLogo /> {loading ? 'Signing in…' : 'Continue with Google'}
          </button>
        )}

        {/* Microsoft SSO removed pending backend support. The email sign-in
            link below already covers Microsoft/Yahoo/any host without
            provider-specific OAuth. To re-add native MS SSO later: restore the
            button JSX + onMicrosoft prop + MicrosoftLogo. */}

        {/* When Google is offered, "Remember me" sits above the OR divider so it
            reads as governing both. When it's the only path, it moves below the
            email form (rendered further down). */}
        {googleEnabled && rememberField}

        {/* ── or, email sign-in link (provider-agnostic) ─────────────────── */}
        {googleEnabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 16px' }}>
            <div style={{ flex: 1, height: 1, background: '#ece9ef' }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: '#b0aabd' }}>OR</span>
            <div style={{ flex: 1, height: 1, background: '#ece9ef' }} />
          </div>
        )}

        {sent ? (
          <div style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'rgba(127,63,152,0.06)', border: '1px solid rgba(127,63,152,0.2)',
            fontSize: 13, color: '#5a2e6e', lineHeight: 1.6, textAlign: 'center',
          }}>
            <strong>Check your inbox.</strong> If <span style={{ fontWeight: 600 }}>{email.trim()}</span> has console
            access, a one-time sign-in link is on its way. It expires in 15 minutes.
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => { setSent(false) }}
                style={{ background: 'none', border: 'none', color: '#7F3F98', fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
              >
                Use a different email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitEmail}>
            <input
              type="email" inputMode="email" autoComplete="email"
              value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={sending}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '13px 14px', borderRadius: 12,
                border: '1.5px solid #ece9ef', fontFamily: 'inherit', fontSize: 14, color: '#231F20',
                marginBottom: 10, outline: 'none',
              }}
              onFocus={e => { e.currentTarget.style.border = '1.5px solid #7F3F98' }}
              onBlur={e => { e.currentTarget.style.border = '1.5px solid #ece9ef' }}
            />
            <button
              type="submit"
              disabled={sending || !email.trim()}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-btn)',
                background: '#7F3F98', border: '1.5px solid #7F3F98',
                color: '#ffffff', fontFamily: 'inherit', fontWeight: 700, fontSize: 14,
                cursor: sending || !email.trim() ? 'not-allowed' : 'pointer',
                opacity: sending || !email.trim() ? 0.6 : 1,
                transition: 'opacity 150ms',
              }}
            >
              {sending ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}

        {/* Google absent → this is the only path; keep "Remember me" reachable
            for it, below the form. Hidden once the link has been sent. */}
        {!googleEnabled && !sent && (
          <div style={{ marginTop: 12 }}>{rememberField}</div>
        )}

        {error && (
          <div role="alert" style={{
            margin: '16px 0 0',
            padding: '11px 14px',
            borderRadius: 10,
            background: 'rgba(237,28,36,0.08)',
            border: '1px solid rgba(237,28,36,0.25)',
            fontSize: 12, color: '#b91c1c', lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid #ece9ef', margin: '24px 0 18px' }} />
        <p style={{ fontSize: 12, color: '#8a8595', textAlign: 'center', lineHeight: 1.65, margin: 0 }}>
          Access is restricted to authorized users.<br />
          Contact your administrator if you need access.
        </p>
      </div>
    </div>
  )
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20H42v-.1H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5c-10.8 0-19.5 8.7-19.5 19.5S13.2 43.5 24 43.5c10 0 19-7.5 19-19.5 0-1.4-.1-2.7-.4-4z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5c-7.6 0-14.2 4.3-17.7 10.2z"/>
      <path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.4l-6.5-5.5C29.7 34 27 35 24 35c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.2 16.3 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20H42v-.1H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.5 5.5c-.5.4 6.8-5 6.8-15.1 0-1.4-.1-2.7-.4-4z"/>
    </svg>
  )
}
